// Error model shared by the API client and the UI.
//
// Server error bodies carry a bounded `code`; the UI translates known codes and
// falls back to a generic message. Raw server messages and debug output are
// never shown as interface copy.

/** Error codes the backend may return, plus client-only categories. */
export type ApiErrorCode =
  | 'invalid_request'
  | 'unauthenticated'
  | 'forbidden'
  | 'claim_rejected'
  | 'device_owned_elsewhere'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'payload_too_large'
  | 'unprocessable'
  | 'internal_error'
  | 'unavailable'
  // Client-only categories.
  | 'network_error'
  | 'invalid_response'
  | 'aborted';

export interface ApiErrorInit {
  code: ApiErrorCode;
  status?: number;
  /** Retry delay from `Retry-After`, in seconds, when provided. */
  retryAfterSeconds?: number;
  /** Server message, kept for diagnostics only; never rendered directly. */
  serverMessage?: string;
  cause?: unknown;
}

/** A typed API failure. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly serverMessage: string | undefined;

  constructor(init: ApiErrorInit) {
    super(init.serverMessage ?? init.code);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.serverMessage = init.serverMessage;
    if (init.cause !== undefined) {
      this.cause = init.cause;
    }
  }

  /** Whether the session is no longer valid and the user must sign in again. */
  get isUnauthenticated(): boolean {
    return this.code === 'unauthenticated';
  }

  /** Whether the caller lacks permission for the operation. */
  get isForbidden(): boolean {
    return this.code === 'forbidden';
  }

  /** Whether the resource is missing or hidden from this principal. */
  get isNotFound(): boolean {
    return this.code === 'not_found';
  }

  /** Whether retrying the same request could plausibly succeed. */
  get isRetryable(): boolean {
    return (
      this.code === 'network_error' ||
      this.code === 'unavailable' ||
      this.code === 'rate_limited' ||
      this.code === 'internal_error'
    );
  }
}

/** Map an HTTP status to a fallback code when the body is missing or malformed. */
export function codeFromStatus(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'invalid_request';
    case 401:
      return 'unauthenticated';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 413:
      return 'payload_too_large';
    case 422:
      return 'unprocessable';
    case 429:
      return 'rate_limited';
    case 503:
      return 'unavailable';
    default:
      return status >= 500 ? 'internal_error' : 'invalid_request';
  }
}

const KNOWN_CODES: ReadonlySet<string> = new Set<ApiErrorCode>([
  'invalid_request',
  'unauthenticated',
  'forbidden',
  'claim_rejected',
  'device_owned_elsewhere',
  'not_found',
  'conflict',
  'rate_limited',
  'payload_too_large',
  'unprocessable',
  'internal_error',
  'unavailable',
]);

/** Whether a decoded string is a code the client understands. */
export function isKnownErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === 'string' && KNOWN_CODES.has(value);
}
